import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatearFecha } from "../utils/formatters";
import { estadoColor } from "../utils/estadoColor";
import { registrarTareaReparacion, usarRepuestoReparacion, depositarEquipo, sacarDeDeposito as apiSacarDeposito, finalizarReparacion as apiFinalizar } from "../services/dataService";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import SectionHeader from "../components/ui/SectionHeader";

export default function ModuloReparacion({ 
  equipos, 
  setEquipos, 
  tickets, 
  setTickets,
  bitacoraReparacion, 
  setBitacoraReparacion,
  repuestos = [],
  setRepuestos,
  registrarMovimientoStock,
  registrarAuditoria,
  onNotify 
}) {
  const { usuario } = useAuth();
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);
  const [nuevaTarea, setNuevaTarea] = useState("");
  const [depositoMotivo, setDepositoMotivo] = useState("");
  const [mostrarFormDeposito, setMostrarFormDeposito] = useState(false);
  
  // 🆕 Estados para repuestos utilizados
  const [showRepuestosSelector, setShowRepuestosSelector] = useState(false);
  const [repuestoSeleccionado, setRepuestoSeleccionado] = useState("");
  const [cantidadRepuesto, setCantidadRepuesto] = useState(1);
  const [repuestosUsados, setRepuestosUsados] = useState([]);

  // Filtrar equipos en reparación o en depósito
  const equiposEnReparacion = equipos.filter(e => 
    e.estado === "En reparación" || e.estado === "En depósito"
  ).sort((a, b) => new Date(b.ingreso) - new Date(a.ingreso));

  const agregarTarea = async () => {
    if (!equipoSeleccionado || !nuevaTarea.trim()) {
      onNotify?.("Seleccioná un equipo y escribí una tarea", "warning");
      return;
    }

    try {
      const saved = await registrarTareaReparacion({
        equipo_id: equipoSeleccionado.id,
        equipo_serie: equipoSeleccionado.serie,
        descripcion: nuevaTarea,
        tecnico: usuario?.nombre || "Sistema"
      });

      setBitacoraReparacion(prev => [saved, ...prev]);
      setNuevaTarea("");
      registrarAuditoria?.(`Tarea registrada en ${equipoSeleccionado.serie}: "${nuevaTarea.slice(0, 40)}"`, "Reparación");
      onNotify?.("Tarea registrada en la bitácora", "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const agregarRepuestoUsado = async () => {
    if (!equipoSeleccionado || !repuestoSeleccionado || cantidadRepuesto < 1) {
      onNotify?.("Seleccioná un repuesto y cantidad válida", "warning");
      return;
    }
  
    const repuesto = repuestos.find(r => 
      String(r.id) === String(repuestoSeleccionado)
    );
    
    if (!repuesto) {
      onNotify?.(`❌ Repuesto no encontrado. Verificá que exista en inventario.`, "error");
      return;
    }
  
    const stockDisp = Number(repuesto.stock) || 0;
    const cantSolicitada = Number(cantidadRepuesto) || 0;
    if (stockDisp < cantSolicitada) {
      onNotify?.(`⚠️ Stock insuficiente: ${repuesto.nombre} tiene ${stockDisp} u. disponibles. Querés usar ${cantSolicitada}`, "error");
      return;
    }
  
    try {
      const entrada = await usarRepuestoReparacion({
        equipo_id: equipoSeleccionado.id,
        equipo_serie: equipoSeleccionado.serie,
        repuesto_id: repuesto.id,
        nombre: repuesto.nombre,
        cantidad: cantSolicitada,
        tecnico: usuario?.nombre || "Sistema",
        precio_unitario: repuesto.precio_venta || 0
      });

      if (setRepuestos) {
        setRepuestos(prev => prev.map(r => 
          String(r.id) === String(repuesto.id) 
            ? { ...r, stock: r.stock - cantSolicitada } 
            : r
        ));
      }

      if (registrarMovimientoStock) {
        registrarMovimientoStock(
          repuesto.id,
          repuesto.nombre,
          -cantSolicitada,
          `Uso en reparación - Equipo ${equipoSeleccionado.serie}`
        );
      }

      setBitacoraReparacion(prev => [entrada, ...prev]);
  
      setRepuestosUsados(prev => [...prev, {
        id: Date.now(),
        nombre: repuesto.nombre,
        lote: repuesto.lote,
        cantidad: cantSolicitada,
        stock_restante: repuesto.stock - cantSolicitada
      }]);
  
      registrarAuditoria?.(`Repuesto usado: ${repuesto.nombre} × ${cantSolicitada} en ${equipoSeleccionado.serie}`, "Reparación");
      setRepuestoSeleccionado("");
      setCantidadRepuesto(1);
      setShowRepuestosSelector(false);
      
      onNotify?.(`✅ ${repuesto.nombre} × ${cantSolicitada} registrado y descontado del inventario`, "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const ponerEnDeposito = async () => {
    if (!equipoSeleccionado || !depositoMotivo.trim()) {
      onNotify?.("Ingresá el motivo del depósito", "warning");
      return;
    }

    try {
      await depositarEquipo({ equipo_id: equipoSeleccionado.id, motivo: depositoMotivo });

      setEquipos(prev => prev.map(e => 
        e.id === equipoSeleccionado.id ? { ...e, estado: "En depósito" } : e
      ));

      const entradaDeposito = {
        id: Date.now(),
        equipo_id: equipoSeleccionado.id,
        equipo_serie: equipoSeleccionado.serie,
        tipo: "Depósito",
        descripcion: `Equipo pasado a depósito. Motivo: ${depositoMotivo}`,
        tecnico: usuario?.nombre || "Sistema",
        fecha: new Date().toLocaleString('es-AR'),
        timestamp: Date.now(),
        motivo: depositoMotivo
      };

      setBitacoraReparacion(prev => [entradaDeposito, ...prev]);
      registrarAuditoria?.(`Equipo ${equipoSeleccionado.serie} → Depósito: ${depositoMotivo}`, "Reparación");
      setDepositoMotivo("");
      setMostrarFormDeposito(false);
      onNotify?.(`Equipo ${equipoSeleccionado.serie} pasado a depósito`, "info");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const sacarDeDeposito = async (equipo) => {
    try {
      await apiSacarDeposito({ equipo_id: equipo.id });

      setEquipos(prev => prev.map(e => 
        e.id === equipo.id ? { ...e, estado: "En reparación" } : e
      ));

      const entradaSalida = {
        id: Date.now(),
        equipo_id: equipo.id,
        equipo_serie: equipo.serie,
        tipo: "Salida de depósito",
        descripcion: "Equipo sacado de depósito - Vuelve a reparación",
        tecnico: usuario?.nombre || "Sistema",
        fecha: new Date().toLocaleString('es-AR'),
        timestamp: Date.now()
      };

      setBitacoraReparacion(prev => [entradaSalida, ...prev]);
      registrarAuditoria?.(`Equipo ${equipo.serie} sacado de depósito → Reparación`, "Reparación");
      onNotify?.(`Equipo ${equipo.serie} vuelto a reparación`, "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const finalizarReparacion = async () => {
    if (!equipoSeleccionado) return;
    if (equipoSeleccionado.estado === "En depósito") {
      onNotify?.("⛔ Sacá el equipo de depósito antes de finalizar la reparación.", "warning");
      return;
    }
    if (!window.confirm(`¿Finalizar reparación de ${equipoSeleccionado.serie}?`)) return;

    const ticketVinculado = tickets.find(t => 
      String(t.equipoId) === String(equipoSeleccionado.id) && t.estado === "Reparación"
    );

    try {
      await apiFinalizar({ equipo_id: equipoSeleccionado.id, ticket_id: ticketVinculado?.id });

      setEquipos(prev => prev.map(e =>
        e.id === equipoSeleccionado.id ? { ...e, estado: "Reparado" } : e
      ));

      if (ticketVinculado && setTickets) {
        setTickets(prev => prev.map(t =>
          String(t.id) === String(ticketVinculado.id) ? { ...t, estado: "Reparado" } : t
        ));
      }

      const entrada = {
        id: Date.now(),
        equipo_id: equipoSeleccionado.id,
        equipo_serie: equipoSeleccionado.serie,
        tipo: "Finalización",
        descripcion: "✅ Reparación finalizada. Equipo marcado como Reparado.",
        tecnico: usuario?.nombre || "Sistema",
        fecha: new Date().toLocaleString('es-AR'),
        timestamp: Date.now()
      };
      setBitacoraReparacion(prev => [entrada, ...prev]);
      registrarAuditoria?.(`Reparación finalizada: ${equipoSeleccionado.serie}`, "Reparación");
      onNotify?.(`✅ ${equipoSeleccionado.serie} marcado como Reparado`, "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const obtenerBitacoraEquipo = (equipoId) => {
    return bitacoraReparacion
      .filter(b => b.equipo_id === equipoId)
      .sort((a, b) => b.timestamp - a.timestamp);
  };

  // 🆕 Filtrar repuestos con stock disponible
  const repuestosDisponibles = repuestos.filter(r => r.stock > 0);

  return (
    <div>
      <SectionHeader 
        title="Reparación y Bitácora" 
        subtitle="Gestioná las tareas de reparación y el estado de los equipos"
      />

      <div className="grid grid--2 gap-4">
        {/* COLUMNA IZQUIERDA: Lista de equipos */}
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            🔧 Equipos en Reparación/Depósito ({equiposEnReparacion.length})
          </h3>
          
          <div className="space-y-2">
            {equiposEnReparacion.map(eq => {
              const ticketVinculado = tickets.find(t => t.equipoId === eq.id);
              const tareasPendientes = obtenerBitacoraEquipo(eq.id).filter(b => b.tipo === "Tarea").length;
              
              return (
                <Card 
                  key={eq.id} 
                  className={`cursor-pointer transition hover:shadow-md ${
                    equipoSeleccionado?.id === eq.id ? "border-2 border-primary" : ""
                  } ${eq.estado === "En depósito" ? "border-l-4 border-warning" : ""}`}
                  onClick={() => setEquipoSeleccionado(eq)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex gap-2 items-center mb-1">
                        <strong className="text-sm">{eq.serie}</strong>
                        <Badge color={estadoColor(eq.estado)}>{eq.estado}</Badge>
                      </div>
                      <p className="text-xs text-muted">{eq.modelo}</p>
                      {ticketVinculado && (
                        <p className="text-xs text-primary mt-1">🎫 Ticket #{ticketVinculado.id}</p>
                      )}
                      {eq.estado === "En depósito" && (
                        <p className="text-xs text-warning mt-1">⏸️ En depósito</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted">{tareasPendientes} tareas</p>
                    </div>
                  </div>
                </Card>
              );
            })}
            
            {equiposEnReparacion.length === 0 && (
              <Card className="text-center py-6">
                <p className="text-muted">No hay equipos en reparación</p>
              </Card>
            )}
          </div>
        </div>

        {/* COLUMNA DERECHA: Bitácora y acciones */}
        <div>
          {equipoSeleccionado ? (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  📋 Bitácora - {equipoSeleccionado.serie}
                </h3>
                <Btn small variant="default" onClick={() => setEquipoSeleccionado(null)}>
                  ✕ Cerrar
                </Btn>
              </div>

              {/* Info del equipo */}
              <Card className="mb-4 bg-surface">
                <div className="grid grid--2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-muted">Modelo</p>
                    <p className="font-medium">{equipoSeleccionado.modelo}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Cliente</p>
                    <p className="font-medium">{equipoSeleccionado.cliente}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Ingreso</p>
                    <p>{formatearFecha(equipoSeleccionado.ingreso)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Estado</p>
                    <Badge color={estadoColor(equipoSeleccionado.estado)}>
                      {equipoSeleccionado.estado}
                    </Badge>
                  </div>
                </div>
              </Card>

              {/* Acciones rápidas */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {equipoSeleccionado.estado === "En depósito" ? (
                  <Btn variant="success" onClick={() => sacarDeDeposito(equipoSeleccionado)}>
                    ✅ Sacar de Depósito
                  </Btn>
                ) : (
                  <Btn variant="warning" onClick={() => setMostrarFormDeposito(true)}>
                    ⏸️ Pasar a Depósito
                  </Btn>
                )}
                {equipoSeleccionado.estado === "En reparación" && (
                  <Btn variant="success" onClick={finalizarReparacion}>
                    ✅ Finalizar Reparación
                  </Btn>
                )}
              </div>

              {/* Formulario de depósito */}
              {mostrarFormDeposito && (
                <Card className="mb-4 border-warning">
                  <p className="font-medium mb-2 text-warning">⏸️ Pasar equipo a depósito</p>
                  <Input
                    value={depositoMotivo}
                    onChange={e => setDepositoMotivo(e.target.value)}
                    placeholder="Motivo (ej: Esperando repuesto, Esperando autorización...)"
                    className="mb-2"
                  />
                  <div className="flex gap-2">
                    <Btn variant="warning" small onClick={ponerEnDeposito}>Confirmar</Btn>
                    <Btn variant="default" small onClick={() => setMostrarFormDeposito(false)}>Cancelar</Btn>
                  </div>
                </Card>
              )}

              {/* 🆕 SELECTOR DE REPUESTOS UTILIZADOS */}
              <Card className="mb-4 border-primary">
                <div className="flex justify-between items-center mb-2">
                  <p className="font-medium">🔧 Repuestos Utilizados</p>
                  <Btn small variant="default" onClick={() => setShowRepuestosSelector(!showRepuestosSelector)}>
                    {showRepuestosSelector ? "✕ Cerrar" : "+ Agregar"}
                  </Btn>
                </div>

                {showRepuestosSelector && (
                  <div className="p-3 bg-surface rounded mb-3">
                    <div className="grid grid--2 gap-2 mb-2">
                    <Select 
                        value={repuestoSeleccionado} 
                        onChange={e => setRepuestoSeleccionado(e.target.value)}
                      >
                        <option value="">Seleccionar repuesto *</option>
                        {repuestosDisponibles.map(r => (
                          <option key={String(r.id)} value={String(r.id)}>
                            {r.nombre} {r.lote && `· Lote ${r.lote}`} · Stock: {r.stock}
                          </option>
                        ))}
                        {repuestosDisponibles.length === 0 && (
                          <option disabled>No hay repuestos con stock disponible</option>
                        )}
                      </Select>
                      <Input 
                        type="number" 
                        min="1" 
                        value={cantidadRepuesto} 
                        onChange={e => setCantidadRepuesto(parseInt(e.target.value) || 1)}
                        placeholder="Cantidad"
                        style={{ width: "100px" }}
                      />
                    </div>
                    <Btn variant="primary" small onClick={agregarRepuestoUsado} disabled={!repuestoSeleccionado}>
                      ✅ Registrar Uso
                    </Btn>
                  </div>
                )}

                {/* Lista de repuestos usados en esta sesión */}
                {repuestosUsados.length > 0 && (
                  <div className="space-y-1">
                    {repuestosUsados.map(ru => (
                      <div key={ru.id} className="flex justify-between items-center text-sm p-2 bg-surface rounded">
                        <span>
                          <strong>{ru.nombre}</strong> {ru.lote && <span className="text-muted">· Lote {ru.lote}</span>}
                        </span>
                        <Badge color="primary">× {ru.cantidad}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                {repuestosUsados.length === 0 && !showRepuestosSelector && (
                  <p className="text-muted text-sm text-center py-2">Sin repuestos registrados aún</p>
                )}
              </Card>

              {/* Formulario de nueva tarea */}
              <Card className="mb-4">
                <p className="font-medium mb-2">📝 Agregar tarea/nota</p>
                <textarea
                  value={nuevaTarea}
                  onChange={e => setNuevaTarea(e.target.value)}
                  placeholder="Describí la tarea realizada o nota importante..."
                  rows={3}
                  className="input w-full mb-2"
                  style={{ resize: "vertical", fontFamily: "inherit" }}
                />
                <Btn variant="primary" onClick={agregarTarea} disabled={!nuevaTarea.trim()}>
                  💾 Guardar en Bitácora
                </Btn>
              </Card>

              {/* Timeline de la bitácora */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm mb-2">📅 Historial de actividades</h4>
                {obtenerBitacoraEquipo(equipoSeleccionado.id).map(entrada => (
                  <Card key={entrada.id} className={`border-l-4 ${
                    entrada.tipo === "Tarea" ? "border-primary" :
                    entrada.tipo === "Depósito" ? "border-warning" :
                    entrada.tipo === "Repuesto" ? "border-success" :
                    "border-muted"
                  }`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex gap-2 items-center mb-1 flex-wrap">
                          <Badge color={
                            entrada.tipo === "Tarea" ? "primary" :
                            entrada.tipo === "Depósito" ? "warning" :
                            entrada.tipo === "Repuesto" ? "success" :
                            "muted"
                          } small>
                            {entrada.tipo}
                          </Badge>
                          <span className="text-xs text-muted">{entrada.fecha}</span>
                          {entrada.tipo === "Repuesto" && entrada.cantidad && (
                            <Badge color="primary" small>× {entrada.cantidad}</Badge>
                          )}
                        </div>
                        <p className="text-sm" style={{ whiteSpace: "pre-wrap" }}>{entrada.descripcion}</p>
                        <p className="text-xs text-muted mt-1">👤 {entrada.tecnico}</p>
                      </div>
                    </div>
                  </Card>
                ))}
                
                {obtenerBitacoraEquipo(equipoSeleccionado.id).length === 0 && (
                  <p className="text-muted text-sm text-center py-4">
                    Sin registros en la bitácora
                  </p>
                )}
              </div>
            </div>
          ) : (
            <Card className="text-center py-12">
              <p className="text-4xl mb-2">🔧</p>
              <p className="text-muted">Seleccioná un equipo para ver su bitácora</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}